import { LOINC, resolveId } from "@medplum/core";
import {
  Attachment,
  Bundle,
  BundleEntry,
  Consent,
  Patient,
  Provenance,
  QuestionnaireResponse,
  Reference,
  Signature,
} from "@medplum/fhirtypes";

import { addDays, formatISO } from "date-fns";

import { Extensions } from "@/fhir/enums/extensions";
import { FHIRValueSets, SignatureType } from "@/fhir/enums/fhir-valuesets";
import { getOrganizationReference, joinReferenceString } from "@/utils/medplum";

import {
  CONSENT_AGREEMENT_LINK_ID,
  CONSENT_TEXT_LINK_ID,
  FULL_NAME_LINK_ID,
  RECORDED_LINK_ID,
  SIGNATURE_LINK_ID,
} from "../constants/questionnaire-link-ids";

const RECORD_LIFECYCLE_SYSTEM = "http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle";

const buildPatientOrganizationCompartment = (
  patientReference: Reference<Patient>,
  practiceId: string,
): Reference[] => [patientReference, { reference: getOrganizationReference(practiceId) }];

const createSignatureStructure = (
  patientReference: Reference<Patient>,
  base64Signature: string | undefined,
): Signature | undefined => {
  if (!base64Signature || !patientReference) return;

  const signature: Signature = {
    type: [
      {
        system: FHIRValueSets.SignatureType,
        code: SignatureType.ConsentSignature,
        display: "Consent Signature",
      },
    ],
    when: new Date().toISOString(),
    who: patientReference,
    data: base64Signature,
  };

  return signature;
};

const createAttachmentStructure = (base64Signature: string | undefined): Attachment | undefined => {
  if (!base64Signature) return;

  const attachment: Attachment = {
    creation: new Date().toISOString(),
    data: base64Signature,
  };

  return attachment;
};

export const createQuestionnaireResponseResourceBundleEntry = (
  questionnaireIds: string[],
  patientReference: Reference<Patient>,
  questionnaireResponseFullUrl: string[],
  formValues: Record<
    string,
    { fullName?: string; recorded?: string; signature?: string; consentAgreement: boolean }
  >,
  practiceId: string,
): BundleEntry<QuestionnaireResponse>[] => {
  const questionnairesResponsesBundle: BundleEntry<QuestionnaireResponse>[] = [];

  Object.keys(formValues).forEach((formKey, index) => {
    const patientSignature = createSignatureStructure(
      patientReference,
      formValues[formKey]?.signature,
    );

    const signatureAttachment = createAttachmentStructure(formValues[formKey]?.signature);

    const userAgreement = formValues[formKey]?.consentAgreement;

    const questionnaireResponse: QuestionnaireResponse = {
      resourceType: "QuestionnaireResponse",
      questionnaire: joinReferenceString("Questionnaire", questionnaireIds[index]),
      status: "completed",
      authored: formatISO(new Date()),
      source: patientReference,
      subject: patientReference,
      item: [
        {
          linkId: CONSENT_TEXT_LINK_ID,
          text: "consent text example",
        },
        {
          linkId: CONSENT_AGREEMENT_LINK_ID,
          text: "Agree to Privacy Policy",
          answer: [{ valueBoolean: formValues[formKey].consentAgreement }],
        },
        ...(formValues[formKey].fullName && userAgreement
          ? [
              {
                linkId: FULL_NAME_LINK_ID,
                text: "Full Name",
                answer: [{ valueString: formValues[formKey].fullName }],
              },
            ]
          : []),
        ...(formValues[formKey].recorded && userAgreement
          ? [
              {
                linkId: RECORDED_LINK_ID,
                text: "Today's Date",
                answer: [{ valueDateTime: formValues[formKey].recorded }],
              },
            ]
          : []),
        ...(formValues[formKey].signature && userAgreement
          ? [
              {
                linkId: SIGNATURE_LINK_ID,
                text: "Signature",
                answer: [
                  {
                    valueAttachment: signatureAttachment,
                  },
                ],
              },
            ]
          : []),
      ],
      extension: [
        ...(formValues[formKey].signature && userAgreement
          ? [{ url: Extensions.QuestionnaireResponseSignature, valueSignature: patientSignature }]
          : []),
        {
          url: Extensions.QuestionnaireResponseIsCompleted,
          valueBoolean: true,
        },
      ],
      meta: { compartment: buildPatientOrganizationCompartment(patientReference, practiceId) },
    };

    questionnairesResponsesBundle.push({
      resource: questionnaireResponse,
      fullUrl: questionnaireResponseFullUrl[index],
      request: {
        method: "POST",
        url: "QuestionnaireResponse",
      },
    });
  });

  return questionnairesResponsesBundle;
};

export const createConsentResourceBundleEntry = (
  patientReference: Reference<Patient>,
  questionnaireResponseFullUrlData: string[],
  consentAgreement: Record<string, { consentAgreement: boolean }>,
  practiceId: string,
  consentFullUrl: string[],
): BundleEntry<Consent>[] => {
  const consentsBundle: BundleEntry<Consent>[] = [];

  questionnaireResponseFullUrlData.forEach((questionnaireResponseFullUrl, index) => {
    const isAccepted = Object.values(consentAgreement)[index]?.consentAgreement;

    const consent: Consent = {
      resourceType: "Consent",
      status: isAccepted ? "active" : "rejected",
      patient: patientReference,
      performer: [patientReference],
      dateTime: new Date().toISOString(),
      sourceReference: getQuestionnaireResponseReference(questionnaireResponseFullUrl),
      scope: {
        coding: [
          {
            system: FHIRValueSets.ConsentScope,
            code: "patient-privacy",
            display: "Privacy Consent",
          },
        ],
      },
      category: [
        {
          coding: [
            {
              system: LOINC,
              code: "64292-6",
              display: "Release of information consent Document",
            },
          ],
        },
      ],
      policyRule: {
        coding: [
          {
            system: FHIRValueSets.PolicyRule,
            code: "hipaa-restrictions",
            display: "HIPAA Restrictions",
          },
        ],
      },
      meta: { compartment: buildPatientOrganizationCompartment(patientReference, practiceId) },
      provision: {
        type: "permit",
        period: {
          start: new Date().toISOString(),
          end: addDays(new Date(), 365).toISOString(),
        },
      },
    };

    consentsBundle.push({
      resource: consent,
      fullUrl: consentFullUrl[index],
      request: {
        method: "POST",
        url: "Consent",
      },
    });
  });
  return consentsBundle;
};

/**
 * Retires each consent a new submission replaces, so the prior signed record stays readable in the
 * client's chart while only the newest one reads as current.
 *
 * The entry carries the whole prior resource with `status` flipped, because a FHIR PUT replaces the
 * resource and a partial body would drop required fields. Consents already retired produce no entry,
 * which keeps a repeated submit idempotent, and ids are deduped because a transaction rejects the
 * same resource identity twice.
 */
export const createSupersededConsentBundleEntries = (
  previousConsents: Consent[],
): BundleEntry<Consent>[] => {
  const supersededConsentIds = new Set<string>();

  return previousConsents.reduce<BundleEntry<Consent>[]>((entries, previousConsent) => {
    const consentId = previousConsent.id;

    if (!consentId || supersededConsentIds.has(consentId)) return entries;
    if (previousConsent.status !== "active" && previousConsent.status !== "rejected")
      return entries;

    supersededConsentIds.add(consentId);

    entries.push({
      resource: { ...previousConsent, status: "inactive" },
      request: {
        method: "PUT",
        url: joinReferenceString("Consent", consentId),
      },
    });

    return entries;
  }, []);
};

/**
 * Records who authored each consent submission.
 *
 * Targets are the transaction's own `urn:uuid` full urls; the server rewrites them to real
 * references once the transaction commits. A submission that retires an earlier consent also
 * targets that consent, so the supersession itself is auditable.
 *
 * Retired consents are keyed by the response that replaces them, so a multi-consent intake keeps
 * each form's audit trail to its own records.
 */
export const createProvenanceResourceBundleEntry = (
  patientReference: Reference<Patient>,
  practiceId: string,
  questionnaireResponseBundle: BundleEntry<QuestionnaireResponse>[],
  consentBundle: BundleEntry<Consent>[],
  supersededConsentsByResponseFullUrl: Record<string, BundleEntry<Consent>[]> = {},
) => {
  const provenanceBundle: BundleEntry<Provenance>[] = [];

  questionnaireResponseBundle.forEach((bundleEntry) => {
    const questionnaireResponseFullUrl = bundleEntry.fullUrl;

    const supersededConsentTargets = (
      supersededConsentsByResponseFullUrl[questionnaireResponseFullUrl ?? ""] ?? []
    ).reduce<Reference[]>((targets, supersededEntry) => {
      const supersededConsentId = supersededEntry.resource?.id;
      if (supersededConsentId) {
        targets.push({ reference: joinReferenceString("Consent", supersededConsentId) });
      }
      return targets;
    }, []);

    const consentFullUrl = consentBundle.find(
      (consent) => consent.resource?.sourceReference?.reference === questionnaireResponseFullUrl,
    )?.fullUrl;

    if (!questionnaireResponseFullUrl || !consentFullUrl) return;

    const provenance: Provenance = {
      resourceType: "Provenance",
      target: [
        { reference: questionnaireResponseFullUrl },
        { reference: consentFullUrl },
        ...supersededConsentTargets,
      ],
      recorded: new Date().toISOString(),
      agent: [
        {
          type: {
            coding: [
              {
                system: "http://terminology.hl7.org/CodeSystem/provenance-participant-type",
                code: "author",
                display: "Author",
              },
            ],
          },
          who: patientReference,
        },
      ],
      reason: [
        {
          coding: [
            {
              system: "http://terminology.hl7.org/CodeSystem/v3-ActReason",
              code: "PAT",
              display: "Patient request",
            },
          ],
        },
      ],
      activity: {
        coding: [
          supersededConsentTargets.length
            ? {
                system: RECORD_LIFECYCLE_SYSTEM,
                code: "amend",
                display: "Amend (Update) Record Lifecycle Event",
              }
            : {
                system: RECORD_LIFECYCLE_SYSTEM,
                code: "originate",
                display: "Originate/Retain Record Lifecycle Event",
              },
        ],
      },
      meta: { compartment: buildPatientOrganizationCompartment(patientReference, practiceId) },
    };

    provenanceBundle.push({
      resource: provenance,
      request: {
        method: "POST",
        url: "Provenance",
      },
    });
  });

  return provenanceBundle;
};

const getQuestionnaireResponseReference = (id: string) => {
  return id.startsWith("urn:uuid:")
    ? { reference: id }
    : { reference: joinReferenceString("QuestionnaireResponse", id) };
};

/**
 * Groups the consents in a `QuestionnaireResponse` + `_revinclude` search by the questionnaire they
 * answer, so a submission can retire every consent it replaces.
 *
 * Consents are keyed to a questionnaire through their response, which needs both resource types
 * resolved before they can be paired — hence the two passes over the bundle.
 */
export const getPreviousConsentsByQuestionnaireId = (bundle: Bundle): Record<string, Consent[]> => {
  const questionnaireIdByResponseId: Record<string, string> = {};
  const consentEntries: Consent[] = [];

  bundle.entry?.forEach((entry) => {
    if (entry.resource?.resourceType === "QuestionnaireResponse") {
      const questionnaireId = resolveId({ reference: entry.resource.questionnaire });
      if (entry.resource.id && questionnaireId) {
        questionnaireIdByResponseId[entry.resource.id] = questionnaireId;
      }
      return;
    }

    if (entry.resource?.resourceType === "Consent") {
      consentEntries.push(entry.resource);
    }
  });

  return consentEntries.reduce<Record<string, Consent[]>>((consentsByQuestionnaireId, consent) => {
    const questionnaireResponseId = resolveId(consent.sourceReference);
    if (!questionnaireResponseId) return consentsByQuestionnaireId;

    const questionnaireId = questionnaireIdByResponseId[questionnaireResponseId];
    if (!questionnaireId) return consentsByQuestionnaireId;

    consentsByQuestionnaireId[questionnaireId] = [
      ...(consentsByQuestionnaireId[questionnaireId] ?? []),
      consent,
    ];

    return consentsByQuestionnaireId;
  }, {});
};
